/**
 * Sweeping one part along a frame axis until it meets the parts in its way.
 * Plain numbers, no Babylon; every part in the same frame.
 */

import { sizeOf, type FitPart, type StudGrid } from './parts';
import type { FitSolid, Vec3 } from './solids';

/** A travel along a frame axis, as seat takes it. */
export type SeatDirection = '-y' | '+y' | '-x' | '+x' | '-z' | '+z';
export const SEAT_DIRECTIONS: readonly SeatDirection[] = ['-y', '+y', '-x', '+x', '-z', '+z'];

/** A part the swept part touches where it stops: the area they touch across the travel, and the knobs seated between them. */
export interface SweepContact<K> {
    readonly part: K;
    readonly area: number;
    readonly knobs: number;
    /** It touches only the tops of that part's studs (of `grid`, which it did not engage), not its body. */
    readonly tops: boolean;
}

export interface Sweep<K> {
    /** How far the part moves along the travel to its first contact; negative when it backs out of an overlap against it. */
    readonly t: number;
    readonly contacts: SweepContact<K>[];
    /** The parts it overlapped where it was put, knobs aside. */
    readonly startedInside: K[];
    /**
     * Of those, the ones that hold it rather than lie ahead of it: they reach both the part's ends along the travel, in
     * one span (a tube, a bore, a pocket) or, when their box holds its box, in different spans or columns (a ring in a
     * hub split by its bore), or it is slender along the travel and runs right through them (a rod through a hub). Backing out of
     * them is no seat.
     */
    readonly held: K[];
    /** Nothing is in its way: `t` is 0 and there are no contacts. */
    readonly nothing: boolean;
    /** The part's own area across the travel, which `contacts[].area` is a share of. */
    readonly across: number;
}

/** Columns along the longer side of the part's shadow. */
const GRID = 40;

interface Column { mover: Span[]; other: Span[] }
/**
 * Where one solid crosses a column, along the travel axis. `knob`: a knob along the travel, which may be
 * inside the other part (a stud seated in it); `facing`: one that points at the other part, so it is counted.
 */
interface Span { lo: number; hi: number; owner: number; solid: FitSolid; knob: boolean; facing: boolean }

/**
 * Move `part` along `dir` (a unit frame axis) from where it is until it touches the first of `others` in its
 * way; if it starts inside one, it backs out against `dir` first (`held` names those that hold it, which `seat`
 * leaves in place). Knobs along the travel (the studs of the part below, the part's own studs) may be inside the
 * other part: they are connectors, and those pointing across the contact are counted. Judged on columns across
 * the travel, so slopes and curves stop it where their surface is, not their box; two open shells (a ring, a
 * bracket) meet where their surfaces do. With `grid`, the model's studs, a stop on stud tops alone is marked `tops`.
 */
export function sweepContact<K>(part: FitPart, others: ReadonlyMap<K, FitPart>, dir: Vec3, grid: StudGrid | null = null): Sweep<K> {
    const a = [0, 1, 2].reduce((m, k) => (Math.abs(dir[k]) > Math.abs(dir[m]) ? k : m), 0);
    const s = dir[a] < 0 ? -1 : 1;
    const [b, c] = [0, 1, 2].filter(k => k !== a);
    const lo = part.lo, hi = part.hi;
    const near = [...others.keys()].filter(k => {
        const o = others.get(k)!;
        return o !== part && o.lo[b] < hi[b] && o.hi[b] > lo[b] && o.lo[c] < hi[c] && o.hi[c] > lo[c];
    });
    const eb = hi[b] - lo[b], ec = hi[c] - lo[c];
    const cell = Math.max(eb, ec) / GRID || 1e-9;
    const nb = Math.max(1, Math.ceil(eb / cell)), nc = Math.max(1, Math.ceil(ec / cell));
    const gb = eb / nb, gc = ec / nc;
    const columns: Column[] = Array.from({ length: nb * nc }, () => ({ mover: [], other: [] }));
    const cast = (p: FitPart, owner: number, toward: number): void => {
        const knobs = new Map(p.knobs.filter(k => k.axis === a).map(k => [k.solid, k.sign === toward]));
        for (const solid of p.solids) {
            if (solid.hi[b] < lo[b] || solid.lo[b] > hi[b] || solid.hi[c] < lo[c] || solid.lo[c] > hi[c]) continue;
            spans(solid, { owner, knob: knobs.has(solid), facing: knobs.get(solid) === true }, { a, b, c, lo, nb, nc, gb, gc }, owner < 0 ? 'mover' : 'other', columns);
        }
    };
    cast(part, -1, s);
    near.forEach((k, i) => cast(others.get(k)!, i, -s));

    const eps = Math.max(sizeOf(lo, hi), 1e-9) * 1e-4;
    /** The travel distances over which mover span `m` overlaps span `o`. */
    const overlapAt = (m: Span, o: Span): [number, number] => (s > 0 ? [o.lo - m.hi, o.hi - m.lo] : [m.lo - o.hi, m.hi - o.lo]);
    /** Whether spans meet over a stretch of travel, or at one point when both are surfaces of open shells. */
    const meets = (m: Span, o: Span, from: number, to: number): boolean => to - from > 2 * eps || (m.lo === m.hi && o.lo === o.hi);
    const blocked: [number, number][] = [];
    const inside = new Set<number>(), held = new Set<number>(), leads = new Set<number>(), trails = new Set<number>();
    const slender = hi[a] - lo[a] >= 3 * Math.max(eb, ec), flush = Math.min(sizeOf(lo, hi) * 0.01, 0.25 * (hi[a] - lo[a]));
    let shadow = 0;
    for (const col of columns) {
        if (col.mover.some(m => !m.knob)) shadow++;
        for (const m of col.mover) {
            for (const o of col.other) {
                if (m.knob || o.knob) continue;
                const [from, to] = overlapAt(m, o);
                if (!meets(m, o, from, to)) continue;
                blocked.push([from, to]);
                if (from < -eps && to > eps) {
                    inside.add(o.owner);
                    // Held: the other reaches both of the part's own ends along the travel, in one span (a tube, a bore, a
                    // pocket) or in two (a hub split by its bore, round a ring on the axle), or the part is slender and runs
                    // right through the other. Not held: only its leading end is sunk (put too far), or a broad part runs
                    // through a thin one. An end flush with the other's face (a plug as long as its bore) counts as held.
                    const holds = (x: number): boolean => o.lo <= x + flush && o.hi >= x - flush;
                    const lead = holds(s < 0 ? lo[a] : hi[a]), trail = holds(s < 0 ? hi[a] : lo[a]);
                    if (lead) leads.add(o.owner);
                    if (trail) trails.add(o.owner);
                    if ((lead && trail) || (!lead && !trail && slender)) held.add(o.owner);
                }
            }
        }
    }
    // Reaching its ends in different spans or columns holds it only inside the other's box (a ring in a wheel's hub); a part
    // that sticks out of it was put there, not held.
    for (const i of leads) if (trails.has(i) && boxHolds(others.get(near[i])!, part)) held.add(i);
    const merged = mergeIntervals(blocked);
    const cellArea = gb * gc;
    const across = shadow * cellArea;
    const containing = merged.find(([x, y]) => x < -eps && y > eps);
    const ahead = merged.filter(([x]) => x >= -eps).sort((p, q) => p[0] - q[0])[0];
    const startedInside = [...inside].map(i => near[i]), heldBy = [...held].map(i => near[i]);
    if (!containing && !ahead) return { t: 0, contacts: [], startedInside, held: heldBy, nothing: true, across };
    const t = containing ? containing[0] : ahead[0];
    // Touching where it stops: a body span of another part starts within a hair of the stop, per column.
    const touch = Math.max(eps * 20, sizeOf(lo, hi) * 2e-3);
    const area = new Map<number, number>();
    const knobs = new Map<number, Set<FitSolid>>();
    const touched = new Map<number, Set<FitSolid>>();
    for (const col of columns) {
        const hit = new Set<number>();
        for (const m of col.mover) {
            for (const o of col.other) {
                const [from, to] = overlapAt(m, o);
                if (m.knob || o.knob) {
                    if ((m.facing || o.facing) && from < t - eps && to > t + eps) {
                        const set = knobs.get(o.owner) ?? new Set<FitSolid>();
                        set.add(m.knob ? m.solid : o.solid);
                        knobs.set(o.owner, set);
                    }
                } else if (meets(m, o, from, to) && Math.abs(from - t) <= touch) {
                    hit.add(o.owner);
                    const set = touched.get(o.owner) ?? new Set<FitSolid>();
                    set.add(o.solid);
                    touched.set(o.owner, set);
                }
            }
        }
        for (const i of hit) area.set(i, (area.get(i) ?? 0) + cellArea);
    }
    const owners = new Set([...area.keys(), ...knobs.keys()]);
    const face = (x: { lo: Vec3; hi: Vec3 }): number => (x.hi[b] - x.lo[b]) * (x.hi[c] - x.lo[c]);
    const studSized = (x: FitSolid): boolean => !!grid?.width && [b, c].every(k => Math.abs(x.hi[k] - x.lo[k] - grid.width) <= 0.15 * grid.width);
    const tops = (i: number): boolean => {
        const o = others.get(near[i])!, hit = [...touched.get(i) ?? []];
        return !knobs.has(i) && o.solids.length > 1 && hit.length > 0 && hit.every(x => face(x) <= 0.25 * face(o) && studSized(x));
    };
    /** Knobs counted once per stud: a cap stacked on a stud is inside its shadow. */
    const studs = (set: ReadonlySet<FitSolid> | undefined): number => {
        const kept: FitSolid[] = [];
        for (const x of [...set ?? []].sort((p, q) => face(q) - face(p))) {
            if (!kept.some(y => y.lo[b] <= x.lo[b] && y.hi[b] >= x.hi[b] && y.lo[c] <= x.lo[c] && y.hi[c] >= x.hi[c])) kept.push(x);
        }
        return kept.length;
    };
    const contacts = [...owners].map(i => ({ part: near[i], area: area.get(i) ?? 0, knobs: studs(knobs.get(i)), tops: tops(i) }))
        .sort((p, q) => q.area - p.area || q.knobs - p.knobs);
    return { t, contacts, startedInside, held: heldBy, nothing: false, across };
}

/** Whether `outer`'s box holds `inner`'s, to 1% of `inner`'s size. */
function boxHolds(outer: FitPart, inner: FitPart): boolean {
    const tol = 0.01 * sizeOf(inner.lo, inner.hi);
    return [0, 1, 2].every(k => outer.lo[k] <= inner.lo[k] + tol && outer.hi[k] >= inner.hi[k] - tol);
}

function mergeIntervals(list: [number, number][]): [number, number][] {
    const sorted = [...list].sort((p, q) => p[0] - q[0]);
    const out: [number, number][] = [];
    for (const [x, y] of sorted) {
        const last = out[out.length - 1];
        if (last && x <= last[1]) last[1] = Math.max(last[1], y);
        else out.push([x, y]);
    }
    return out;
}

interface Grid { a: number; b: number; c: number; lo: Vec3; nb: number; nc: number; gb: number; gc: number }

/**
 * Where `solid` crosses each column through the grid's cells, as spans along axis `a`. A closed solid's hits
 * pair up into entry and exit; an open one (a plane) is a span of zero length at each hit.
 */
function spans(solid: FitSolid, tag: Pick<Span, 'owner' | 'knob' | 'facing'>, g: Grid, side: keyof Column, columns: Column[]): void {
    const { a, b, c, lo, nb, nc, gb, gc } = g;
    const p = solid.positions, tri = solid.shape.triangles;
    const hits = new Map<number, number[]>();
    for (let t = 0; t < tri.length; t += 3) {
        const i0 = tri[t] * 3, i1 = tri[t + 1] * 3, i2 = tri[t + 2] * 3;
        const x0 = p[i0 + b], y0 = p[i0 + c], x1 = p[i1 + b], y1 = p[i1 + c], x2 = p[i2 + b], y2 = p[i2 + c];
        const d = (y1 - y2) * (x0 - x2) + (x2 - x1) * (y0 - y2);
        if (Math.abs(d) < 1e-30) continue;
        // Column centres are nudged off the grid's lines, so rays miss the edges of axis-aligned faces.
        const iFrom = Math.max(0, Math.floor((Math.min(x0, x1, x2) - lo[b]) / gb - 0.5)), iTo = Math.min(nb - 1, Math.ceil((Math.max(x0, x1, x2) - lo[b]) / gb - 0.5));
        const jFrom = Math.max(0, Math.floor((Math.min(y0, y1, y2) - lo[c]) / gc - 0.5)), jTo = Math.min(nc - 1, Math.ceil((Math.max(y0, y1, y2) - lo[c]) / gc - 0.5));
        for (let i = iFrom; i <= iTo; i++) {
            const u = lo[b] + (i + 0.5137) * gb;
            for (let j = jFrom; j <= jTo; j++) {
                const v = lo[c] + (j + 0.4909) * gc;
                const l0 = ((y1 - y2) * (u - x2) + (x2 - x1) * (v - y2)) / d, l1 = ((y2 - y0) * (u - x2) + (x0 - x2) * (v - y2)) / d, l2 = 1 - l0 - l1;
                if (l0 < 0 || l1 < 0 || l2 < 0) continue;
                const h = l0 * p[i0 + a] + l1 * p[i1 + a] + l2 * p[i2 + a];
                const key = i * nc + j;
                const list = hits.get(key);
                if (list) list.push(h); else hits.set(key, [h]);
            }
        }
    }
    for (const [key, list] of hits) {
        list.sort((x, y) => x - y);
        const out = columns[key][side];
        if (solid.shape.closed) for (let k = 0; k + 1 < list.length; k += 2) out.push({ lo: list[k], hi: list[k + 1], solid, ...tag });
        else for (const h of list) out.push({ lo: h, hi: h, solid, ...tag });
    }
}
